/* eslint-disable @typescript-eslint/naming-convention */
import { LayoutModule } from '@angular/cdk/layout';
import { ChangeDetectorRef, Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDatepicker } from '@angular/material/datepicker';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DTypeForm } from '@grupo-constant/next-dtype/form';
import { SelectOption, SelectOptionGroup } from '@grupo-constant/next-dtype/types';
import { NgbTimepickerModule, NgbTimeStruct } from '@ng-bootstrap/ng-bootstrap';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { compareAsc, format, parse, parseISO } from 'date-fns';
import { cloneDeep } from 'lodash-es';
import { combineLatest, debounceTime, firstValueFrom, map, Observable, startWith, Subject, takeUntil } from 'rxjs';
import { ComponentsModule } from '../../components.module';
import { SharedModule } from '../../../../shared/shared.module';
import { FuseCardModule } from '../../../../../@fuse/components/card/card.module';
import { FuseAlertModule } from '../../../../../@fuse/components/alert/alert.module';
import { DTypeModule } from '../../../dtype/dtype.module';
import { GenericApplicationName, NextServicio } from '../../../../modules/admin/services/services.types';
import { CompanyDataUser, FilterAddress, PlanningAreaAsOption, RetrieveUserCalendarReply, TaskGoal, UserCalendar, UserData, UserZombies, WorkDay, ZombieData, Task, FlushTaskBody, TaskStatusEnum, ChangeServiceUserAssignmentTrainedBody, RetrieveNextTaskCustomGoalAutoAssignmentRequest, RetrieveUserCalendarBody, CancelReason, CancelTaskListBody } from '../../../../modules/admin/tasks/tasks.types';
import { NextTaskType } from '../../../task-type/task-type.types';
import { LocalizePipe } from '../../../pipes/localize.pipe';
import { DialogsService } from '../../../../dialogs/dialogs.service';
import { ServicesService } from '../../../../modules/admin/services/services.service';
import { TaskTypeService } from '../../../task-type/task-type.service';
import { TasksService } from '../../../../modules/admin/tasks/tasks.service';
import { DTypeService } from '../../../dtype/dtype.service';
import { FuseConfirmationService } from '../../../../../@fuse/services/confirmation/confirmation.service';
import { Utils } from '../../../utils/utils';

@Component({
    selector: 'edit-task-sidebar',
    templateUrl: './edit-task-sidebar.component.html',
    styles: [
        /* language=SCSS */
        `
            .green-icon {
                color: green;
            }
            .red-icon {
                color: red;
            }
            .selectbg1 {
                color:orange;
            }

            @keyframes appearAnimationKeyframes {
                from {
                    opacity: 0;
                }
                to {
                    opacity: 1;
                }
            }

            .appear-animation {
                animation: appearAnimationKeyframes 1000ms ease-in-out;
            }
        `
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        SharedModule,
        TranslocoModule,
        LayoutModule,
        FuseCardModule,
        FuseAlertModule,
        NgbTimepickerModule,
        DTypeModule,
        ComponentsModule
    ]
})
export class EditTaskSidebarComponent implements OnInit, OnDestroy {

    @Output() readonly closeUnchanged: EventEmitter<void> = new EventEmitter<void>();
    @Output() readonly update: EventEmitter<void> = new EventEmitter<void>();
    @Output() readonly closeEdit: EventEmitter<void> = new EventEmitter<void>();

    task: Task;
    service: NextServicio;

    updatingTask: boolean = false;

    // Update form variables
    taskForm: FormGroup;
    dateToday: string;

    planningAreas: PlanningAreaAsOption[];
    taskTypes: SelectOptionGroup[] | any;
    taskTypeData: NextTaskType;
    taskTypeDTypeForm: DTypeForm;
    hideTaskTypeDTypeForm = false;
    goals: TaskGoal[] = [];
    autoCalculatedGoals = false;

    flashMessage: 'success' | 'error' | null = null;
    hasChanges = false;
    isNew: boolean = false;

    addressFilter: FilterAddress;
    addressOptions: any[] = [];

    selectedStartDate: string | Date = '';
    selectedPlanningAreaId: string = '';
    loadingUserZombiesCalendar: boolean = false;

    userZombiesCalendar: UserZombies;
    filteredZombies: Observable<ZombieData[]>;
    filteredUsers: Observable<UserData[]>;

    calendarBelongsTo: string = '';
    selectedDayUserTasks: UserCalendar[] = [];
    selectedDayAbsence: {
        isAbsence: boolean,
        fromTime?: string,
        toTime?: string,
    };
    selectedDayIsHoliday: boolean;
    nextDayUserTasks: UserCalendar[] = [];
    cachedUserCalendar: Map<string, Map<string, RetrieveUserCalendarReply>> = new Map();
    cachedZombieCalendar: number[] = [];

    selectedUser: UserData;
    selectedZombie: ZombieData;
    selectedTaskTypeId: string;

    changeUserZombieLink: boolean = false;

    workDayData: any[] = [];
    workDay: WorkDay;
    isCopy: boolean = false;
    resetByTaskType: boolean = true;

    disableButton: boolean = false;
    initialUserId: string;
    userChanged: boolean = false;

    isTaskEndsNextDay: boolean = false;
    isTaskMoreThan8h: boolean = false;

    availableUsers: CompanyDataUser[] = [];
    alreadyAlertedCopy: boolean = false;

    autoUser: boolean = false;
    autoZombie: boolean = false;
    animateCalendar: boolean = false;
    weekDays: string[] = null;

    GenericApplicationName = GenericApplicationName;

    private unsubcribeAll: Subject<void> = new Subject();

    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private localize: LocalizePipe,
        private dialogService: DialogsService,
        private tr: TranslocoService,
        private servicesService: ServicesService,
        private taskTypeService: TaskTypeService,
        private tasksService: TasksService,
        private dtypeService: DTypeService,
        private _snackbar: MatSnackBar,
        private _fuseConfirmationService: FuseConfirmationService,
        private dialogsService: DialogsService,
        private transloco: TranslocoService,
    ) {
        // Create the task form
        this.taskForm = new FormGroup({
            taskId: new FormControl<string>('', Validators.required),
            planningAreaId: new FormControl<string>('', Validators.required),
            planningAreaName: new FormControl<string>(''),
            taskTypeId: new FormControl<string>('', Validators.required),
            scheduled_from_date: new FormControl<string>('', Validators.required),
            scheduled_from_time: new FormControl<NgbTimeStruct | string>(undefined),
            scheduled_from_time2: new FormControl<NgbTimeStruct | string>(undefined),
            scheduled_to_date: new FormControl<string>(''),
            scheduled_to_time: new FormControl<NgbTimeStruct | string>(undefined),
            scheduled_to_time2: new FormControl<NgbTimeStruct | string>(undefined),
            addressOid: new FormControl<string>(''),
            externalTaskId: new FormControl<string>(''),
            workDayOid: new FormControl<string>(''),
            workDayUri: new FormControl<string>(''),
            zombie: new FormControl<ZombieData>(undefined),
            user: new FormControl<UserData>(undefined),
        });
    }

    async ngOnInit(): Promise<void> {
        this.dateToday = new Date().toISOString().split('T')[0];
        this.availableUsers = await firstValueFrom(this.tasksService.availableUsers$);

        // Subscriptions to controls value changes
        this.taskForm.get('taskTypeId').valueChanges
            .pipe(takeUntil(this.unsubcribeAll))
            .subscribe(async (value) => {
                await this.loadTaskTypeData(value);
                this.resetByTaskType = true;

            });

        this.taskForm.get('workDayOid').valueChanges.pipe(takeUntil(this.unsubcribeAll), debounceTime(500),).subscribe(async (value) => {
            if (value) {
                if (value !== this.workDay?._oid) {
                    this.workDay = this.workDayData.find(w => w._oid === value);
                }
                switch (this.workDay?.mode) {
                    case 'SPECIFIC_SCHEDULE':
                        this.taskForm.get('scheduled_from_time2').setValue(undefined);
                        this.taskForm.get('scheduled_to_time2').setValue(undefined);

                        this.taskForm.get('scheduled_from_time2').clearValidators();
                        this.taskForm.get('scheduled_to_time2').clearValidators();
                        this.taskForm.get('scheduled_from_time').addValidators(Validators.required);
                        this.taskForm.get('scheduled_to_time').addValidators(Validators.required);

                        this.taskForm.get('scheduled_from_time2').updateValueAndValidity();
                        this.taskForm.get('scheduled_to_time2').updateValueAndValidity();
                        this.taskForm.get('scheduled_from_time').updateValueAndValidity();
                        this.taskForm.get('scheduled_to_time').updateValueAndValidity();

                        this.taskForm.get('scheduled_from_time').setValue(this.workDay.from);
                        this.taskForm.get('scheduled_to_time').setValue(this.workDay.to);
                        break;
                    case 'MINUTES':
                        if (!this.taskForm.get('scheduled_to_time').value && this.taskForm.get('scheduled_from_time').value) {
                            this.taskForm.get('scheduled_to_time').setValue(this.changeDuration(this.taskForm.get('scheduled_from_time').value,
                                Number(this.workDay.duration), 'ADD'));
                        } else if (this.taskForm.get('scheduled_to_time').value && !this.taskForm.get('scheduled_from_time').value) {
                            this.taskForm.get('scheduled_from_time').setValue(this.changeDuration(this.taskForm.get('scheduled_to_time').value,
                                Number(this.workDay.duration), 'REST'));
                        }
                        this.taskForm.get('scheduled_from_time2').setValue(undefined);
                        this.taskForm.get('scheduled_to_time2').setValue(undefined);
                        this.taskForm.get('scheduled_from_time2').clearValidators();
                        this.taskForm.get('scheduled_to_time2').clearValidators();
                        this.taskForm.get('scheduled_from_time').addValidators(Validators.required);
                        this.taskForm.get('scheduled_to_time').addValidators(Validators.required);
                        break;
                    case 'SPLIT':
                        this.taskForm.get('scheduled_from_time2').addValidators(Validators.required);
                        this.taskForm.get('scheduled_to_time2').addValidators(Validators.required);
                        this.taskForm.get('scheduled_from_time').addValidators(Validators.required);
                        this.taskForm.get('scheduled_to_time').addValidators(Validators.required);

                        this.taskForm.get('scheduled_from_time').setValue(this.workDay.from);
                        this.taskForm.get('scheduled_to_time').setValue(this.workDay.to);
                        this.taskForm.get('scheduled_from_time2').setValue(this.workDay.from2);
                        this.taskForm.get('scheduled_to_time2').setValue(this.workDay.to2);
                        break;
                    default:
                        this.taskForm.get('scheduled_from_time2').setValue(undefined);
                        this.taskForm.get('scheduled_to_time2').setValue(undefined);

                        this.taskForm.get('scheduled_from_time2').clearValidators();
                        this.taskForm.get('scheduled_to_time2').clearValidators();
                        if (this.taskTypeData?.requiresPlanningStartTime) {
                            this.taskForm.get('scheduled_from_time').addValidators(Validators.required);
                        }
                        if (this.taskTypeData?.requiresPlanningEndTime) {
                            this.taskForm.get('scheduled_to_time').addValidators(Validators.required);
                        }

                        this.taskForm.get('scheduled_from_time2').updateValueAndValidity();
                        this.taskForm.get('scheduled_to_time2').updateValueAndValidity();
                        this.taskForm.get('scheduled_from_time').updateValueAndValidity();
                        this.taskForm.get('scheduled_to_time').updateValueAndValidity();

                }
            } else {
                this.workDay = undefined;
                this.taskForm.get('scheduled_from_time2').setValue(undefined);
                this.taskForm.get('scheduled_to_time2').setValue(undefined);

                this.taskForm.get('scheduled_from_time2').clearValidators();
                this.taskForm.get('scheduled_to_time2').clearValidators();
                if (this.taskTypeData?.requiresPlanningStartTime) {
                    this.taskForm.get('scheduled_from_time').addValidators(Validators.required);
                }
                if (this.taskTypeData?.requiresPlanningEndTime) {
                    this.taskForm.get('scheduled_to_time').addValidators(Validators.required);
                }

                this.taskForm.get('scheduled_from_time2').updateValueAndValidity();
                this.taskForm.get('scheduled_to_time2').updateValueAndValidity();
                this.taskForm.get('scheduled_from_time').updateValueAndValidity();
                this.taskForm.get('scheduled_to_time').updateValueAndValidity();
            }
        });

        this.taskForm.get('scheduled_from_time').valueChanges.pipe(takeUntil(this.unsubcribeAll), debounceTime(2000),).subscribe(async (value) => {
            if (this.workDay?.mode === 'MINUTES' && value) {
                this.taskForm.get('scheduled_to_time').setValue(this.changeDuration(value, Number(this.workDay.duration), 'ADD'));
            }
        });

        this.taskForm.get('scheduled_to_time').valueChanges.pipe(takeUntil(this.unsubcribeAll), debounceTime(2000),).subscribe(async (value) => {
            if (this.workDay?.mode === 'MINUTES' && value) {
                this.taskForm.get('scheduled_from_time').setValue(this.changeDuration(value, Number(this.workDay.duration), 'REST'));
            }
        });

        combineLatest([this.taskForm.get('scheduled_from_time').valueChanges, this.taskForm.get('scheduled_to_time').valueChanges])
            .pipe(takeUntil(this.unsubcribeAll)).subscribe(([valueFrom, valueTo]) => {
                if (this.taskTypeData?.typology === 'WITHOUT_TIME' || this.taskTypeData?.typology === 'TASK_ASSIGNMENT') {
                    this.isTaskEndsNextDay = false;
                    this.isTaskMoreThan8h = false;
                    return;
                }
                if (valueFrom && valueTo && valueFrom > valueTo) {
                    this.isTaskEndsNextDay = true;
                } else {
                    this.isTaskEndsNextDay = false;
                }
                if (valueFrom && valueTo) {
                    if (valueFrom <= valueTo) {
                        if (this.getDiffHours(valueTo, valueFrom) > 8) {
                            this.isTaskMoreThan8h = true;
                        } else {
                            this.isTaskMoreThan8h = false;
                        }
                    } else {
                        if (24 - this.getDiffHours(valueFrom, valueTo) > 8) {
                            this.isTaskMoreThan8h = true;
                        } else {
                            this.isTaskMoreThan8h = false;
                        }
                    }
                } else {
                    this.isTaskMoreThan8h = false;
                }
            });

        combineLatest([
            this.taskForm.get('scheduled_from_time').valueChanges,
            this.taskForm.get('scheduled_from_time2').valueChanges,
            this.taskForm.get('scheduled_to_time').valueChanges,
            this.taskForm.get('scheduled_to_time2').valueChanges
        ]).pipe(takeUntil(this.unsubcribeAll)).subscribe(() => {
            this.autoCalculatedGoals = false;
        });

        this.taskForm.get('scheduled_from_date').valueChanges.pipe(takeUntil(this.unsubcribeAll)).subscribe((value) => {
            this.autoCalculatedGoals = false;
            if (!value) {
                this.selectedStartDate = '';
            } else {
                this.selectedStartDate = this.taskForm.get('scheduled_from_date').value;
                if (this.selectedUser) {
                    this.getUserCalendar(this.selectedUser, this.selectedUser.userId);
                }
            }
        });

        this.taskForm.get('addressOid').valueChanges.pipe(takeUntil(this.unsubcribeAll)).subscribe((value) => {
            this.autoCalculatedGoals = false;
        });

        this.taskForm.get('planningAreaId').valueChanges.pipe(takeUntil(this.unsubcribeAll)).subscribe((value) => {
            this.planningAreaSelected(value);
            this.resetUserZombiesVariables();
        });

        combineLatest([this.taskForm.get('addressOid').valueChanges, this.taskForm.get('taskTypeId').valueChanges]).pipe(
            takeUntil(this.unsubcribeAll),
            debounceTime(500)
        ).subscribe(([address, taskType]) => {
            address = address ?? '';
            const locationUri = taskType ? this.addressFilter.taskType[taskType] : undefined;
            if (locationUri && typeof address === 'string' /* && address?.length >= 3 */) {
                this.tasksService.searchAddress(address, locationUri).pipe(takeUntil(this.unsubcribeAll)).subscribe((reply) => {
                    this.addressOptions = reply.data;
                    const findAddress: SelectOption = this.addressOptions.find(a => a._oid === address);
                    if (findAddress) {
                        this.taskForm.get('addressOid').setValue(findAddress);
                    }
                });
            } else {
                this.addressOptions = [];
            }
        });

        combineLatest([this.taskForm.get('planningAreaId').valueChanges, this.taskForm.get('scheduled_from_date').valueChanges]).pipe(
            takeUntil(this.unsubcribeAll),
        ).subscribe(async ([planningAreaId, fromDate]) => {
            if (planningAreaId && fromDate) {
                if (!this.loadingUserZombiesCalendar) {
                    this.loadingUserZombiesCalendar = true;
                    fromDate = typeof fromDate === 'string' ? fromDate : format(fromDate, 'yyyy-MM-dd');
                    this.userZombiesCalendar = await firstValueFrom(this.tasksService.getTaskAssignments(this.service.serviceId, planningAreaId, fromDate));
                    this.taskForm.get('user').setValue(this.taskForm.get('user').value);

                    const zombie = (this.userZombiesCalendar?.zombies ?? []).find(z => z.zombieNumber === this.task?.zombieNumber);
                    const user = (this.userZombiesCalendar?.users ?? []).find(u => u.userId === this.task?.userId);
                    this.taskForm.get('zombie').setValue(zombie ?? '');
                    if (user) {
                        this.taskForm.get('user').setValue(user ?? '');
                    }

                    //Select users
                    if (this.userZombiesCalendar && planningAreaId !== this.selectedPlanningAreaId) {
                        this.selectedPlanningAreaId = planningAreaId;
                        if (!this.selectedUser) {
                            if (this.userZombiesCalendar?.users.length === 1) {
                                this.selectUser(this.userZombiesCalendar?.users[0], true);
                                this.taskForm.get('user').setValue(user);
                                if (this.selectedUser.zombieNumber > 0 && this.userZombiesCalendar?.zombies.length > 0) {
                                    if (this.userZombiesCalendar?.zombies.find(z => z.zombieNumber === this.selectedUser.zombieNumber)) {
                                        this.selectedZombie = this.userZombiesCalendar?.zombies.find(z => z.zombieNumber === this.selectedUser.zombieNumber);
                                        this.taskForm.get('zombie').setValue(this.selectedZombie);
                                    }
                                }
                            } else {
                                const user = this.userZombiesCalendar?.users.filter(item => item.userId === this.task?.userId)[0];
                                if (user) {
                                    this.selectUser(user, true);
                                    this.taskForm.get('user').setValue(user);
                                }
                            }
                        }
                        if (!this.selectedZombie) {
                            if (this.userZombiesCalendar?.zombies.length === 1) {
                                this.selectedZombie = this.userZombiesCalendar.zombies[0];
                                if (!this.selectedZombie) {
                                    this.selectedZombie = this.userZombiesCalendar.zombies.find(item =>
                                        item.zombieNumber === 0);
                                }
                                this.taskForm.get('zombie').setValue(this.selectedZombie);
                                if (this.selectedZombie.zombieNumber > 0 && this.userZombiesCalendar?.users.length > 0) {
                                    if (this.userZombiesCalendar?.users.find(u => u.zombieNumber === this.selectedZombie.zombieNumber)) {
                                        const user = this.userZombiesCalendar?.users.find(u => u.zombieNumber === this.selectedZombie.zombieNumber);
                                        if (user) {
                                            this.selectUser(user, true);
                                            this.taskForm.get('user').setValue(user);
                                        }
                                    }
                                }
                            } else {
                                this.selectedZombie = this.userZombiesCalendar.zombies.filter(item =>
                                    item.zombieNumber === this.task?.zombieNumber)[0];
                                if (!this.selectedZombie) {
                                    this.selectedZombie = this.userZombiesCalendar.zombies.find(item =>
                                        item.zombieNumber === 0);
                                }
                                this.taskForm.get('zombie').setValue(this.selectedZombie);
                            }
                        }
                    }
                    this.loadingUserZombiesCalendar = false;
                }
            } else {
                this.resetLocalVariables();
                this.autoUser = false;
                this.autoZombie = false;
            }

        });

        this.taskForm.get('zombie').valueChanges
            .pipe(takeUntil(this.unsubcribeAll))
            .subscribe(async (value: string | ZombieData) => {
                if (typeof value !== 'string') {
                    if (value && value !== this.selectedZombie) {
                        this.selectZombie(value);
                    } else {
                        this.autoZombie = false;
                    }
                } else {
                    this.selectedZombie = undefined;
                    this.autoZombie = false;
                }
            });

        this.taskForm.get('user').valueChanges
            .pipe(takeUntil(this.unsubcribeAll))
            .subscribe(async (value: string | UserData) => {
                if (typeof value !== 'string') {
                    if (value && value !== this.selectedUser) {
                        if (value.userId && (value.userId !== this.task?.userId)) {
                            if (!this.availableUsers.find(u => u.userId === value.userId)?.userPrincipalName ||
                                this.availableUsers.find(u => u.userId === value.userId).userPrincipalName === '') {
                                await this.awaitWarn(this.transloco.translate('noEmailMissionUser'));
                            }
                        }
                        if (value.userId !== this.task?.userId && this.task?.userId) {
                            this.userChanged = true;
                        } else {
                            this.userChanged = false;
                        }
                        this.selectUser(value);
                    } else {
                        this.autoUser = false;
                    }
                } else {
                    this.selectedUser = undefined;
                    this.autoUser = false;
                }
            });

        // Set observables for zombies and users autocompletes
        this.filteredZombies = this.taskForm.get('zombie').valueChanges
            .pipe(
                takeUntil(this.unsubcribeAll),
                debounceTime(200),
                startWith(this.taskForm.get('zombie').value ?? ''),
                map((value) => {
                    if (!value || typeof value === 'string') {
                        return this.filterZombies(value ?? '');
                    } else {
                        const zombie = value as ZombieData;
                        return (this.userZombiesCalendar?.zombies ?? []).filter(z => z.zombieNumber === zombie.zombieNumber);
                    }
                })
            );

        this.filteredUsers = this.taskForm.get('user').valueChanges
            .pipe(
                takeUntil(this.unsubcribeAll),
                debounceTime(200),
                startWith(this.taskForm.get('user').value ?? ''),
                map((value) => {
                    if (!value || typeof value === 'string') {
                        return this.filterUsers(value ?? '');
                    } else {
                        const user = value as UserData;
                        return (this.userZombiesCalendar?.users ?? []).filter(u => u.userId === user.userId);
                    }
                })
            );

        this.taskForm.valueChanges.pipe(takeUntil(this.unsubcribeAll))
            .subscribe(() => this.hasChanges = true);

        combineLatest([
            this.tasksService.taskById$,
            this.servicesService.service$,
            this.tasksService.planningAreas$,
            this.tasksService.taskTypesForPlanning$,
            this.tasksService.address$,
            this.tasksService.alreadyAlertedCopy$,
        ]).pipe(takeUntil(this.unsubcribeAll))
            .subscribe(async ([task, service, planningAreas, taskTypes, address, alreadyAlertedCopy]) => {
                this.task = task?.task;
                this.service = service;
                this.weekDays = service.weekDays ?? null;
                this.planningAreas = planningAreas;
                this.taskTypes = cloneDeep(taskTypes);
                this.alreadyAlertedCopy = alreadyAlertedCopy;
                for (const group of this.taskTypes) {
                    group.option = group.option.filter(o => !o._deprecated);
                    if (group.option.length === 0) {
                        this.taskTypes = this.taskTypes.filter(g => g !== group);
                    }
                }
                this.addressFilter = address;

                this.initialUserId = this.task?.userId;

                if (this.task && this.task?.copyCantEdit && !this.alreadyAlertedCopy) {
                    this.tasksService.setAlreadyAlertedCopy(true);
                    this.isCopy = true;
                    const confirm = await this.confirm(this.tr.translate('warningCopyTask', { taskId: this.task?.oldTaskId }));
                    if (!confirm) {
                        this.close();
                    }
                    this.task.copy = false;
                } else if (this.task && this.task?.copy) {
                    this.isCopy = true;
                }

                if (this.task?.taskId && this.task.taskId !== 'new') {
                    this.updatingTask = true;
                    await this.loadTaskTypeData(this.task?.taskTypeId);
                    this.resetLocalVariables();
                    this.populateTaskForm(this.task);
                    this.populateLocalVariables(this.task);
                    this.hasChanges = false;
                } else if (this.task?.taskId === 'new' && !this.isNew) {
                    this.isNew = true;
                    this.updatingTask = true;
                    await this.loadTaskTypeData(this.task?.taskTypeId);
                    this.resetLocalVariables();
                    this.populateTaskForm(this.task);
                    this.populateLocalVariables(this.task);
                    this.hasChanges = false;
                } else {
                    this.updatingTask = false;
                }
            });
    }

    ngOnDestroy(): void {
        this.unsubcribeAll.next();
        this.unsubcribeAll.complete();
    }

    compareFnZombie(option1, option2) {
        return option1?.zombieNumber === option2?.zombieNumber;
    }

    compareFnUser(option1, option2) {
        return option1?.userId === option2?.userId;
    }

    close() {
        this.disableButton = false;
        this.closeUnchanged.emit();
    }

    toggleUpdating(str: string = '') {
        if (str === '') {
            this.updatingTask = !this.updatingTask;
            if (this.updatingTask) {
                this.taskForm.reset(undefined);
                this.resetLocalVariables();
                this.populateTaskForm(this.task);
                this.populateLocalVariables(this.task);
                this.hasChanges = false;
            }
        } else if (str === 'close') {
            this.closeEdit.emit();
        }
    }

    /**
     * Update the selected task using the form data
     */
    async updateSelectedTask() {

        // Get the task object
        this.disableButton = true;
        const task = this.taskForm.getRawValue();
        this.taskForm.markAllAsTouched();

        const valid = this.taskForm.valid;

        // Validate zombies and users
        const validZombieUsers = this.evaluateZombieUsers();

        let extraPayload;
        let taskTypeFormValid = true;
        if (this.taskTypeDTypeForm) {
            this.taskTypeDTypeForm.control.markAllAsTouched();
            extraPayload = this.taskTypeDTypeForm.control.getRawValue();
            taskTypeFormValid = !this.taskTypeDTypeForm.control.invalid;
        }

        if (valid && validZombieUsers && taskTypeFormValid) {
            if (this.userChanged) {
                let confirmChangeUser: boolean = false;
                if (this.initialUserId && this.selectedUser?.userId !== this.initialUserId && this.task.status === 'SENT') {
                    confirmChangeUser = await this.confirm(this.tr.translate('confirmChangeUser'));
                };
                if (!confirmChangeUser && this.task.status === 'SENT') {
                    this.disableButton = false;
                    return;
                }
            }
            await this.calculateGoals();
            const flushTaskBody: FlushTaskBody = {
                taskId: task.taskId !== 'new' ? this.task?.taskId : undefined,
                serviceId: this.service.serviceId,
                taskTypeUri: task.taskTypeUri,
                taskTypeId: task.taskTypeId,
                externalTaskId: task.externalTaskId,
                planningAreaId: task.planningAreaId,
                zombieNumber: this.selectedZombie?.zombieNumber,
                userId: this.selectedUser?.userId ?? this.taskForm.get('user').value,
                addressOid: this.taskTypeData?.locationMaster ? task.addressOid?._oid : null,
                addressUri: this.taskTypeData?.locationMaster ?? null,
                scheduled_from_date: format(typeof task.scheduled_from_date === 'string' ? parseISO(task.scheduled_from_date) : task.scheduled_from_date, 'yyyy-MM-dd'),
                scheduled_to_date: (this.taskTypeData?.typology === 'WITHOUT_TIME' || this.taskTypeData?.typology === 'TASK_ASSIGNMENT') ?
                    format(typeof task.scheduled_to_date === 'string' ? parseISO(task.scheduled_to_date) : task.scheduled_to_date, 'yyyy-MM-dd') : null,
                scheduled_from_time: task.scheduled_from_time,
                scheduled_to_time: task.scheduled_to_time,
                scheduled_from_time2: this.workDay?.mode === 'SPLIT' ? task.scheduled_from_time2 : null,
                scheduled_to_time2: this.workDay?.mode === 'SPLIT' ? task.scheduled_to_time2 : null,
                notice: task.notice,
                changeUserZombieLink: this.changeUserZombieLink,
                workDayTypeOid: this.taskTypeData?.typology === 'WITH_TIME' ? task.workDayOid : null,
                goals: this.goals,
                extraPayload
            };
            this.tasksService.updateTask(flushTaskBody).subscribe(async (reply) => {
                if (reply.task) {
                    // Si se ha copiado una tarea que no se puede editar, se pregunta al usuario si se quiere cancelar la original (solo en estado WORKING)
                    if (this.task.copyCantEdit && this.task.oldTaskId) {
                        try {
                            const taskByIdReply = await firstValueFrom(this.tasksService.retrieveTaskById({ serviceId: this.service.serviceId, taskId: this.task.oldTaskId }));
                            const originalTask = taskByIdReply?.task;
                            if (originalTask && (originalTask.status === TaskStatusEnum.WORKING || originalTask.status === TaskStatusEnum.SENT)) {
                                await this.cancelOriginalTask(originalTask);
                            }
                        } catch (error) {
                            console.error(error);
                        }
                    }
                    // Actualizamos observable de usuarios disponibles
                    const availableUsers = await firstValueFrom(this.tasksService.searchName(this.service.serviceId, '', ''));
                    this.tasksService.setAvailableUsers(availableUsers.companyUser);

                    // Actualizamos observable de tarea copiada
                    this.tasksService.setTaskCopied(reply.task?.taskId);
                    const body = await firstValueFrom(this.tasksService.bodyWithFilters$);

                    // Mensaje de tarea creada/actualizada y cerramos drawer
                    this.hasChanges = false;
                    this.showFlashMessage('success');
                    setTimeout(() => this.close(), 1000);
                } else {
                    this.disableButton = false;
                    this.showFlashMessage('error');
                }
            }, (error) => {
                this.disableButton = false;
                console.error(error);
                this.dialogsService.openErrorDialog({
                    message: error?.error?.echo ? this.localize.transform(error?.error?.echo) : undefined
                });
            });
        } else {
            this.dialogsService.openErrorDialog({
                message: this.transloco.translate('errorInvalidForm')
            });
            this.disableButton = false;
        }
    }

    async addGoalsToTask() {
        await this.calculateGoals();
        const dialog = this.dialogService.openTaskGoalDialog(this.taskTypeData,
            format(typeof this.taskForm.get('scheduled_from_date').value === 'string' ?
            parseISO(this.taskForm.get('scheduled_from_date').value) : this.taskForm.get('scheduled_from_date').value, 'yyyy-MM-dd'), cloneDeep(this.goals), true);
        dialog.afterClosed()
            .pipe(takeUntil(this.unsubcribeAll))
            .subscribe((goals: TaskGoal[]) => {
                if (goals) {
                    this.goals = goals;
                }
            });
    }

    displayAddress(address: any): string {
        return address?.name ? address?.name : '';
    }

    clearAddress() {
        this.taskForm.get('addressOid').setValue(undefined);
    }

    openDatepickerOnClick(datepicker: MatDatepicker<Date>) {
        if (!datepicker.opened) {
            datepicker.open();
        }
    }

    changeUserZombieCheck(check: boolean) {
        this.changeUserZombieLink = check;
    }

    myStartDateFilter = (d: Date | null): boolean => {
        const day = (d || new Date());
        const today = new Date();
        const virtualScheduledDay = new Date(this.task.scheduled_from_date);

        day.setHours(2);
        virtualScheduledDay.setHours(2);

        today.setHours(2);
        today.setMinutes(0);
        today.setSeconds(0);
        today.setMilliseconds(0);

        return (compareAsc(virtualScheduledDay, day) === 0 || compareAsc(today, day) === 0 || day > today);
    };

    myEndDateFilter = (d: Date | null): boolean => {
        const day = (d || new Date());
        const today = new Date();

        day.setHours(2);

        today.setHours(2);
        today.setMinutes(0);
        today.setSeconds(0);
        today.setMilliseconds(0);

        return (compareAsc(today, day) === 0 || day > today);
    };

    async changeTrained($event: MouseEvent, user: UserData) {
        $event.stopPropagation();
        const confirmed = await this.confirm(user.trained ? 'changeUserNotTrained' : 'changeUserTrained');
        if (confirmed) {
            const body: ChangeServiceUserAssignmentTrainedBody = {
                serviceId: this.service.serviceId,
                planningArea: this.taskForm.get('planningAreaId').value,
                userId: user.userId,
                trained: !user.trained
            };
            try {
                const reply = await firstValueFrom(this.tasksService.changeServiceUserAssignmentTrained(body));
                if (reply?.status === 200) {
                    for (let i = 0; this.userZombiesCalendar.users.length; i++) {
                        if (this.userZombiesCalendar.users[i]?.userId === reply.serviceUserAssignment?.userId) {
                            this.userZombiesCalendar.users[i].trained = reply.serviceUserAssignment?.trained;
                            break;
                        }
                    }
                    this._snackbar.open(this.tr.translate('userProfileSaved', { user: user.fullname }), this.tr.translate('accept'), { duration: 5000 });
                } else {
                    this.openWarningDialog(this.tr.translate('errorDialogMsg'));
                }
            } catch (error) {
                console.error(error);
                this.dialogService.openErrorDialog();
            }
        }
    }

    displayValueZombie(zombie: ZombieData) {
        return zombie?.name ? zombie.name : '';
    }

    displayValueUser(user: UserData | string) {
        return typeof user === 'string' ? user : user?.fullname ? user.fullname : '';
    }

    private async loadTaskTypeData(value: string) {
        if (value && value !== this.selectedTaskTypeId) {
            this.selectedTaskTypeId = value;
            this.taskTypeData = await firstValueFrom(this.taskTypeService.retrieveTaskTypeById(value));
            if (this.task?.taskId !== 'new') {
                if (this.taskTypeData?.goals?.length > 0) {
                    this.goals = await firstValueFrom(this.tasksService.retrieveTaskGoal(this.task.taskId, this.service.serviceId));
                    if (this.taskTypeData.goalAutoAssignment) {
                        this.autoCalculatedGoals = true;
                    }
                } else {
                    this.goals = [];
                    this.autoCalculatedGoals = false;
                }
            } else {
                if (this.task?.copy && this.taskTypeData?.goals?.length > 0 && this.task.oldTaskId) {
                    this.goals = await firstValueFrom(this.tasksService.retrieveTaskGoal(this.task.oldTaskId, this.service.serviceId));
                } else {
                    this.goals = [];
                    this.autoCalculatedGoals = false;
                }
            }
            if (this.taskTypeData.dtype) {
                this.taskTypeDTypeForm = this.dtypeService.getDtypeFormFromDtype(this.taskTypeData.dtype,
                    /* Se ha entrado a editar una tarea */(this.task.taskTypeId === value ? this.task?.serverPayload?.extraPayload : /* Se ha modificado el tipo de tarea*/ undefined)
                        ?? {});
                if (Object.values(this.taskTypeDTypeForm.fields).filter(f => !f.hidden).length === 0) {
                    this.hideTaskTypeDTypeForm = true;
                } else {
                    this.hideTaskTypeDTypeForm = false;
                }
            } else {
                this.taskTypeDTypeForm = undefined;
            }
            if (this.resetByTaskType) {
                this.taskForm.get('addressOid').reset();
                this.taskForm.get('workDayOid').reset();
            }
            this.workDayData = [];
            if (this.taskTypeData?.workDayTypeUri) {
                const data = await firstValueFrom(this.dtypeService.retrieveDTypeDataCollectionByUri(this.taskTypeData.workDayTypeUri));
                for (const d of data) {
                    if (!this.workDayData.find(w => w._oid === d._oid)) {
                        this.workDayData.push(d);
                    }
                }
                this.workDay = this.workDayData.find(w => w._oid === this.taskForm.get('workDayOid').value);
            } else {
                this.workDay = undefined;
            }
            if (this.taskTypeData?.typology === 'WITH_TIME' && (!this.workDay || this.workDay?.mode === 'NONE')) {
                this.taskForm.get('scheduled_from_time2').setValue(undefined);
                this.taskForm.get('scheduled_to_time2').setValue(undefined);
                this.taskForm.get('scheduled_to_date').setValue(undefined);

                this.taskForm.get('scheduled_from_time2').clearValidators();
                this.taskForm.get('scheduled_to_time2').clearValidators();
                this.taskForm.get('scheduled_to_date').clearValidators();
                if (this.taskTypeData?.requiresPlanningStartTime) {
                    this.taskForm.get('scheduled_from_time').addValidators(Validators.required);
                }
                if (this.taskTypeData?.requiresPlanningEndTime) {
                    this.taskForm.get('scheduled_to_time').addValidators(Validators.required);
                }

                this.taskForm.get('scheduled_from_time2').updateValueAndValidity();
                this.taskForm.get('scheduled_to_time2').updateValueAndValidity();
                this.taskForm.get('scheduled_from_time').updateValueAndValidity();
                this.taskForm.get('scheduled_to_time').updateValueAndValidity();
            } else if (this.taskTypeData?.typology === 'WITHOUT_TIME' || this.taskTypeData?.typology === 'TASK_ASSIGNMENT') {
                this.taskForm.get('scheduled_from_time2').setValue(undefined);
                this.taskForm.get('scheduled_to_time2').setValue(undefined);

                this.taskForm.get('scheduled_from_time2').clearValidators();
                this.taskForm.get('scheduled_to_time2').clearValidators();
                this.taskForm.get('scheduled_from_time').addValidators(Validators.required);
                this.taskForm.get('scheduled_to_time').addValidators(Validators.required);
                this.taskForm.get('scheduled_to_date').addValidators(Validators.required);

                this.taskForm.get('scheduled_from_time2').updateValueAndValidity();
                this.taskForm.get('scheduled_to_time2').updateValueAndValidity();
                this.taskForm.get('scheduled_from_time').updateValueAndValidity();
                this.taskForm.get('scheduled_to_time').updateValueAndValidity();
                this.taskForm.get('scheduled_to_date').updateValueAndValidity();
            }
        } else if(!value) {
            this.taskTypeData = undefined;
            this.resetUserZombiesVariables();
        }
    }

    private selectZombie(zombie: ZombieData) {
        this.selectedZombie = zombie;
        this.autoZombie = false;
        if (zombie.zombieNumber !== 0 && !this.selectedUser) {
            this.selectedUser = this.userZombiesCalendar?.users.find(item => item.userId === zombie.userId);
            this.taskForm.get('user').setValue(this.selectedUser ?? '');
            this.changeUserZombieLink = false;
            this.autoUser = true;
        }

        if (!this.selectedStartDate) {
            this.selectedStartDate = this.taskForm.get('scheduled_from_date').value;
        }

        if (this.selectedUser && this.calendarBelongsTo !== this.selectedUser?.userId) {
            this.animateCalendar = false;
            this.getUserCalendar(this.selectedUser, this.selectedUser?.userId);
        }

        if (!this.selectedUser && zombie?.zombieNumber > 0 && this.calendarBelongsTo !== zombie?.name) {
            this.animateCalendar = false;
            this.getZombieCalendar(zombie, zombie.name);
        }

        if (!this.selectedUser && (zombie?.zombieNumber === 0 || !zombie)) {
            this.animateCalendar = false;
            this.resetCalendar();
        }
    }

    private selectUser(user: UserData, auto = false) {
        this.selectedUser = user;
        this.autoUser = auto;
        if (user && !this.selectedZombie) {
            this.selectedZombie = this.userZombiesCalendar?.zombies.find(z => z.zombieNumber === user.zombieNumber);
            this.taskForm.get('zombie').setValue(this.selectedZombie ?? '');
            this.changeUserZombieLink = false;
            this.autoZombie = true;
        }

        if (!this.selectedStartDate) {
            this.selectedStartDate = this.taskForm.get('scheduled_from_date').value;
        }

        if (user && this.calendarBelongsTo !== user?.userId) {
            this.animateCalendar = false;
            this.getUserCalendar(user, user?.userId);
        }

        if (!user && this.selectedZombie?.zombieNumber > 0 && this.calendarBelongsTo !== this.selectedZombie?.name) {
            this.animateCalendar = false;
            this.getZombieCalendar(this.selectedZombie, this.selectedZombie?.name);
        }

        if (!user && (this.selectedZombie?.zombieNumber === 0 || !this.selectedZombie)) {
            this.animateCalendar = false;
            this.resetCalendar();
        }
    }

    private async populateLocalVariables(task: Task) {
        if ((task?.taskId !== 'new' && task?.taskId) || (this.isCopy)) {
            let taskId: string;
            if (task?.taskId === 'new' && this.isCopy) {
                taskId = task?.oldTaskId;
            } else {
                taskId = task?.taskId;
            }
        }
    }

    private changeDuration(value: string, minutes: number, action: string) {
        const data = value.split(':');
        let totalHoursInMinutes: number;
        switch (action) {
            case 'ADD':
                totalHoursInMinutes = Number(data[0]) * 60 + minutes + Number(data[1]);
                break;
            case 'REST':
                totalHoursInMinutes = Number(data[0]) * 60 - minutes + Number(data[1]);
                break;
        }
        const hours = Math.floor(totalHoursInMinutes / 60);
        data[0] = String(hours);
        data[1] = String(totalHoursInMinutes % 60);
        data[data.findIndex(d => d === '0')] = '00';
        return data.join(':');
    }

    private getDiffHours(hhmmssFrom: string, hhmmssTo: string): number {
        const dataFrom = hhmmssFrom.split(':');
        const dataTo = hhmmssTo.split(':');
        return Number(dataFrom[0]) + Number(dataFrom[1]) / 60 - Number(dataTo[0]) - Number(dataTo[1]) / 60;
    }

    private populateTaskForm(task: Task) {
        this.resetByTaskType = false;
        this.taskForm.get('taskId').setValue(task?.taskId);
        this.taskForm.get('planningAreaId').setValue(task?.planningAreaId);
        this.taskForm.get('planningAreaName').setValue(task?.planningAreaName);
        this.taskForm.get('taskTypeId').setValue(task?.taskTypeId);
        this.taskForm.get('workDayUri').setValue(task?.workDayTypeUri);
        this.taskForm.get('workDayOid').setValue(task?.workDayTypeOid);
        //CAMBIAR LOS DATES A LOS ORIGINALES EN FUNCION DEL TIMEZONE
        if (task?.scheduled_from_time) {
            this.taskForm.get('scheduled_from_date').setValue(this.getLocalizedDate(task?.scheduled_from_time, task?.planningAreaTimeZoneId));
            this.taskForm.get('scheduled_from_time').setValue(this.getLocalizedTime(task?.scheduled_from_time, task?.planningAreaTimeZoneId));
        } else {
            this.taskForm.get('scheduled_from_date').setValue(task?.scheduled_from_date);
            this.taskForm.get('scheduled_from_time').setValue(task?.scheduled_from_time);
        }
        if (task?.scheduled_to_time) {
            this.taskForm.get('scheduled_to_time').setValue(this.getLocalizedTime(task?.scheduled_to_time, task?.planningAreaTimeZoneId));
            if (task?.scheduled_to_date) {
                this.taskForm.get('scheduled_to_date').setValue(this.getLocalizedDate(task?.scheduled_to_time, task?.planningAreaTimeZoneId));
            }
        } else {
            this.taskForm.get('scheduled_to_date').setValue(task?.scheduled_to_date);
        }
        if (task?.scheduled_from_time2) {
            this.taskForm.get('scheduled_from_time2').setValue(this.getLocalizedTime(task?.scheduled_from_time2, task?.planningAreaTimeZoneId));
        } else {
            this.taskForm.get('scheduled_from_time2').setValue(task?.scheduled_from_time2);
        }
        if (task?.scheduled_to_time2) {
            this.taskForm.get('scheduled_to_time2').setValue(this.getLocalizedTime(task?.scheduled_to_time2, task?.planningAreaTimeZoneId));
        } else {
            this.taskForm.get('scheduled_to_time2').setValue(task?.scheduled_to_time2);
        }
        this.taskForm.get('externalTaskId').setValue(task?.externalTaskId);
        this.taskForm.get('addressOid').setValue(task?.serverPayload?.addressOid ?
            {name: task?.serverPayload?.addressName, address: task?.serverPayload?.address, _oid: task?.serverPayload?.addressOid} : '');
        if (this.userZombiesCalendar) {
            const zombie = (this.userZombiesCalendar.zombies ?? []).find(z => z.zombieNumber === task.zombieNumber);
            const user = (this.userZombiesCalendar.users ?? []).find(u => u.userId === task.userId);
            this.taskForm.get('zombie').setValue(zombie ?? '');
            this.taskForm.get('user').setValue(user ?? this.service.generateUsernamesManually ? task?.zombieName : '');
        } else {
            this.taskForm.get('user').setValue(this.service.generateUsernamesManually ? task?.zombieName : '');
        }
        if (task.taskId === 'new') {
            this.autoSelectUniqueOptions();
        }
    }

    private autoSelectUniqueOptions() {
        if (this.planningAreas.length === 1) {
            this.taskForm.get('planningAreaId').setValue(this.planningAreas[0].code);
            this.taskForm.get('planningAreaName').setValue(this.planningAreas[0].value);
        }
        if (this.taskTypes.length === 1 && this.taskTypes[0].option.length === 1) {
            this.taskForm.get('taskTypeId').setValue(this.taskTypes[0].option[0].code);
        }
    }

    /**
     * Show flash message
     */
    private showFlashMessage(type: 'success' | 'error'): void {
        // Show the message
        this.flashMessage = type;

        // Mark for check
        this._changeDetectorRef.markForCheck();

        // Hide it after 3 seconds
        setTimeout(() => {

            this.flashMessage = null;

            // Mark for check
            this._changeDetectorRef.markForCheck();
        }, 3000);
    }

    private planningAreaSelected(planningAreaId: string) {
        if (this.planningAreas?.length > 0) {
            const planningArea = this.planningAreas.find(pa => pa.code === planningAreaId);
            if (planningArea) {
                this.taskForm.get('planningAreaName').setValue(this.localize.transform(planningArea.value), { emitEvent: false });
            }
        }
    }

    private async calculateGoals() {
        const task = this.taskForm.getRawValue();
        if (!this.autoCalculatedGoals && task.taskTypeId && this.taskTypeData?.goalAutoAssignment && task?.addressOid?._oid && task.scheduled_from_date) {
            const request: RetrieveNextTaskCustomGoalAutoAssignmentRequest = {
                addressOid: task.addressOid._oid,
                task_scheduled_from_date: format(typeof task.scheduled_from_date === 'string' ? parseISO(task.scheduled_from_date) : task.scheduled_from_date, 'yyyy-MM-dd'),
                taskTypeUri: this.taskTypeData.uri
            };
            if (task.workDayOid) {
                request.workdayTypeOid = task.workDayOid;
            }
            if (this.taskTypeData.goalAutoAssignmentDistributeHours) {
                request.task_scheduled_from_time = task.scheduled_from_time;
                request.task_scheduled_from_time2 = task.scheduled_from_time2;
                request.task_scheduled_to_time = task.scheduled_to_time;
                request.task_scheduled_to_time2 = task.scheduled_to_time2;
            }
            const autoGoals = await firstValueFrom(this.tasksService.retrieveNextTaskCustomGoalAutoAssignment(request));
            this.goals = [];
            for (const taskTypeGoal of this.taskTypeData.goals) {
                const scheduledFromDate: Date = typeof task.scheduled_from_date === 'string' ? parseISO(task.scheduled_from_date) : task.scheduled_from_date;
                if (scheduledFromDate >= parseISO(taskTypeGoal.from) && (!taskTypeGoal.to || task.scheduled_from_date <= parseISO(taskTypeGoal.to))) {
                    if (autoGoals[taskTypeGoal.name]) {
                        this.goals.push({
                            goalName: taskTypeGoal.name,
                            goalValue: autoGoals[taskTypeGoal.name],
                        });
                    }
                }
            }
            this.autoCalculatedGoals = true;
        }
    }

    private async getUserCalendar(user: UserData = null, name: string) {
        this.calendarBelongsTo = name ?? '';
        if (user) {
            if (this.selectedStartDate) {
                const date = typeof this.selectedStartDate === 'string' ? this.selectedStartDate : format(this.selectedStartDate, 'yyyy-MM-dd');
                let calendar: RetrieveUserCalendarReply;
                if (!this.cachedUserCalendar.get(date)?.get(user.userId)) {
                    const request: RetrieveUserCalendarBody = {
                        userId: user?.userId,
                        serviceId: this.service.serviceId,
                        planningAreaId: this.selectedPlanningAreaId,
                        date
                    };
                    calendar = await firstValueFrom(this.tasksService.getUserCalendar(request));
                    if (!this.cachedUserCalendar.get(date)) {
                        this.cachedUserCalendar.set(date, new Map());
                    }
                    this.cachedUserCalendar.get(date).set(user.userId, calendar);
                } else {
                    calendar = this.cachedUserCalendar.get(date).get(user.userId);
                }
                if (this.userZombiesCalendar?.users.length > 0 && this.userZombiesCalendar.users.some(item => item.userId === user?.userId)) {
                    this.userZombiesCalendar.users.find(item => item.userId === user?.userId).calendar = calendar;
                    user.calendar = calendar;
                }
            }
            const date = typeof this.selectedStartDate === 'string' ? this.selectedStartDate : format(this.selectedStartDate, 'yyyy-MM-dd');
            this.selectedDayUserTasks = user.calendar?.calendar?.filter(item => item.date === date || item.date === this.dateToday) ?? [];
            this.selectedDayAbsence = user.calendar ? { isAbsence: user.calendar.isAbsence ?? false, fromTime: user.calendar.fromTime, toTime: user.calendar.toTime } : undefined;
            this.selectedDayIsHoliday = user.calendar?.isHoliday ?? false;
            this.nextDayUserTasks = user.calendar?.calendar?.filter(item => item.date !== date && item.date !== this.dateToday) ?? [];
            this.getCalendar();
        } else {
            this.selectedDayUserTasks = [];
            this.selectedDayAbsence = undefined;
            this.selectedDayIsHoliday = false;
            this.nextDayUserTasks = [];
        }
    }

    private async getZombieCalendar(zombie: ZombieData, name: string) {
        this.calendarBelongsTo = name ?? '';
        if (zombie) {
            const date = typeof this.selectedStartDate === 'string' ? this.selectedStartDate : format(this.selectedStartDate, 'yyyy-MM-dd');
            if (!this.cachedZombieCalendar.includes(zombie?.zombieNumber)) {
                this.cachedZombieCalendar.push(zombie?.zombieNumber);
                const calendar = await firstValueFrom(this.tasksService.getZombieCalendar(this.service.serviceId, this.taskForm.get('planningAreaId').value,
                    date, zombie?.zombieNumber));
                if (this.userZombiesCalendar) {
                    this.userZombiesCalendar.zombies.filter(item => item.zombieNumber === zombie?.zombieNumber)[0].calendar = calendar;
                }
            }
            this.selectedDayUserTasks = zombie.calendar?.filter(item => item.date === this.selectedStartDate || item.date === this.dateToday) ?? [];
            this.selectedDayAbsence = undefined;
            this.selectedDayIsHoliday = false;
            this.nextDayUserTasks = zombie.calendar?.filter(item => item.date !== this.selectedStartDate && item.date !== this.dateToday) ?? [];
            this.getCalendar();
        } else {
            this.selectedDayUserTasks = [];
            this.selectedDayAbsence = undefined;
            this.selectedDayIsHoliday = false;
            this.nextDayUserTasks = [];
        }
    }

    private filterZombies(value: string) {
        const filterValue = Utils.removeAccents(value.toLowerCase());
        return (this.userZombiesCalendar?.zombies ?? []).filter(zombie => Utils.removeAccents(zombie.name.toLowerCase()).includes(filterValue));
    }

    private filterUsers(value: string) {
        const filterValue = Utils.removeAccents(value.toLowerCase());
        return (this.userZombiesCalendar?.users ?? []).filter(user => Utils.removeAccents(user.fullname.toLowerCase()).includes(filterValue));
    }

    private getCalendar() {
        if (this.selectedDayUserTasks) {
            for (const userCalendar of this.selectedDayUserTasks) {
                userCalendar.fromTime = userCalendar.fromTime?.split(':').slice(0, 2).join(':');
                userCalendar.toTime = userCalendar.toTime?.split(':').slice(0, 2).join(':');
            }
            for (const userCalendar of this.nextDayUserTasks) {
                userCalendar.fromTime = userCalendar.fromTime?.split(':').slice(0, 2).join(':');
                userCalendar.toTime = userCalendar.toTime?.split(':').slice(0, 2).join(':');
            }
        }
        this.animateCalendar = true;
    }

    private resetUserZombiesVariables() {
        this.resetCalendar();
        this.selectedUser = null;
        this.taskForm.get('user').reset();
        this.selectedZombie = null;
        this.taskForm.get('zombie').reset();
        this.cachedUserCalendar = new Map();
        this.cachedZombieCalendar = [];
        this.changeUserZombieLink = false;
        this.userZombiesCalendar = undefined;
    }

    private resetCalendar() {
        this.calendarBelongsTo = '';
        this.selectedDayUserTasks = [];
        this.selectedDayAbsence = undefined;
        this.selectedDayIsHoliday = false;
        this.nextDayUserTasks = [];
    }

    private resetLocalVariables() {
        this.selectedStartDate = '';
        this.resetUserZombiesVariables();
    }

    private evaluateZombieUsers(): boolean {
        if (this.service.generateUsernamesManually || (!this.selectedUser && !this.selectedZombie) || this.selectedZombie) {
            return true;
        }
        this.dialogService.openErrorDialog({
            title: this.tr.translate('invalidPick'),
            message: this.tr.translate('zombieNeededForUser'),
        });
        this.disableButton = false;
        return false;
    }

    private async cancelOriginalTask(originalTask: Task) {
        const confirm = await this.confirm('confirmCancelOriginalTask');
        if (confirm) {
            const dialog = this.dialogsService.openCancelTaskDialog([originalTask.taskId], []);
            const reason: CancelReason = await firstValueFrom(dialog.afterClosed());
            if (reason) {
                const body: CancelTaskListBody = {
                    serviceId: this.service.serviceId,
                    taskId: [originalTask.taskId],
                    cancellationReason: reason._oid
                };
                try {
                    const reply = await firstValueFrom(this.tasksService.cancelTaskList(body));
                    if (reply.status === 200) {
                        if (reply.unchangedTaskId) {
                            this._snackbar.open(this.transloco.translate('services.taskPlanning.unchangedTasks', { tasks: reply.unchangedTaskId?.length }),
                                this.transloco.translate('accept'), { duration: 5000 });
                        } else {
                            this._snackbar.open(this.transloco.translate('services.taskPlanning.successCancelTask'),
                                this.transloco.translate('accept'), { duration: 5000 });
                        }
                    }
                } catch (error) {
                    console.error(error);
                    this.dialogsService.openErrorDialog({
                        message: error?.echo && Object.keys(error.echo).length ? this.localize.transform(error.echo) : undefined
                    });
                }
            }
        }
    }

    private confirm(_message: string) {
        const dialogRef = this._fuseConfirmationService.open({
            title: this.tr.translate('warning'),
            message: this.tr.translate(_message),
            actions: {
                confirm: {
                    label: this.tr.translate('accept')
                },
                cancel: {
                    label: this.tr.translate('cancel')
                }
            }
        });

        return new Promise<boolean>((resolve) => {
            dialogRef.afterClosed().subscribe(result => resolve(result === 'confirmed'));
        });
    }

    private openWarningDialog(message: string) {
        return this._fuseConfirmationService.open({
            title: this.tr.translate('warning'),
            message: message,
            actions: {
                confirm: {
                    label: this.tr.translate('confirm')
                },
                cancel: {
                    label: this.tr.translate('cancel'),
                    show: false
                }
            }
        });
    }

    private awaitWarn(_message: string) {
        const dialogRef = this._fuseConfirmationService.open({
            title: this.tr.translate('warning'),
            message: this.tr.translate(_message),
            actions: {
                confirm: {
                    label: this.tr.translate('accept')
                },
                cancel: {
                    label: this.tr.translate('cancel'),
                    show: false
                }
            }
        });

        return new Promise<boolean>((resolve) => {
            dialogRef.afterClosed().subscribe(result => resolve(result === 'confirmed'));
        });
    }

    private getLocalizedDate(dateString: string, timeZone: string): Date {
        const date = parseISO(dateString);
        const localeDate = parse(date.toLocaleString('es-ES', { timeZone: timeZone }), 'd/M/y, H:mm:ss', new Date()); // 2/2/2023, 9:00:00
        return localeDate;
    }

    private getLocalizedTime(date: string, timeZone: string): string {
        const formatedDate = parseISO(date);
        const localeTime = formatedDate.toLocaleTimeString('es-ES', { timeZone: timeZone });
        return localeTime;
    }
}
